import { _ } from "../../../core/polyglot/polyglot.js";
import { getAccountTypeFromId, income } from "./AccountType.js";
import AbstractRuleModel from "./AbstractRuleModel.js";

/**
 * Represent how income or cost account impact other accounts in order to have a correct balance.
 * `paymentSpread` is an array of numbers between 0 and 100 and `accountType` is "income" or "cost"
 * `vatRate` is the VAT rate applied to the account.
 *
 * For example, if `accountType` is "income" and `spread` is [30, 50,
 * 20], it means that the company's customers pay in 30% of the times
 * their invoice the first month, in 50% of the times the second month
 * and in 20% of the times the third month.
 *
 * @param {VatRate} vatRate - VAT rate instance
 * @param {int[]} paymentSpread - Numbers between 0 and 100. The sum of these
 *   numbers must be 100
 * @param {AccountType} accountType - Account type conditions how the VAT and cash conversion are calculated.
 */
export default class BalanceRuleModel extends AbstractRuleModel {
	constructor({
		vatRate,
		paymentSpread = [20, 70, 10],
		accountType = income,
	} = {}) {
		super(...arguments);

		this._vatRate = vatRate;
		this._paymentSpread = paymentSpread;
		this._accountType = accountType;
	}

	clone() {
		const context = {
			vatRates: [],
		};

		if (this._vatRate) {
			context.vatRates.push(this._vatRate.clone());
		}

		return this.constructor.materialize(this.serialize(), context);
	}

	getAccountType() {
		return this._accountType;
	}

	getAccountTypeName() {
		return this._accountType.getName();
	}

	setAccountType(accountType) {
		this._accountType = accountType;
	}

	getPaymentSpread() {
		return this._paymentSpread;
	}

	setPaymentSpread(paymentSpread) {
		this._paymentSpread = paymentSpread;
	}

	getVatRate() {
		return this._vatRate;
	}

	getVatRateValue() {
		return this._vatRate?.getValue() ?? 0;
	}

	setVatRate(vatRate) {
		this._vatRate = vatRate;
	}

	isBalance() {
		return true;
	}

	accept(visitor) {
		return visitor.visitBalanceRuleModel(this);
	}

	_serializedProperties() {
		const properties = super._serializedProperties();

		properties.accountType = this._accountType.getType();
		properties.paymentSpread = this._paymentSpread;
		properties.vatRate = this._vatRate?.getValue() ?? 0;

		return properties;
	}

	static transformMaterializationData(data, context) {
		super.transformMaterializationData(...arguments);

		data.accountType = getAccountTypeFromId(data.accountType);

		data.vatRate = context.vatRates.find(
			(vatRate) => vatRate.getValue() === data.vatRate,
		);
	}

	static getLabel() {
		return _("Settings");
	}

	static getDescriptionText() {
		return _("VAT and payment terms on invoices");
	}

	static getType() {
		return "Balance";
	}
}
